import * as React from 'react';
import {
  ApiError,
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
  type AppUserRole
} from '@cdf/api-client-react';

import { useAuth } from '@/components/auth/auth-provider';

/**
 * Session bridge.
 *
 * Adapts the plugin-aware MSAL auth provider (`useAuth`) to the session/role
 * contract consumed by the layout shell. Role and active status come from
 * `GET /api/auth/me` once Entra sign-in succeeds — never from client claims alone.
 *
 * - Auth plugin disabled → signed-in "Local Developer" admin (local dev).
 * - Auth plugin enabled  → MSAL account + server role/active from `/api/auth/me`.
 */

export type SessionStatus = 'loading' | 'signedIn' | 'signedOut';

/** Application roles persisted on app_users (and Local Developer admin). */
export type UserRole = 'admin' | 'teacher' | 'parent' | 'student' | 'pending';

export interface SessionUser {
  name: string;
  email: string;
  initials: string;
  role: UserRole;
  /** False when an admin has deactivated the account. */
  isActive: boolean;
  /** Synced app_users id when persistence is available. */
  appUserId?: string | null;
  avatarUrl?: string;
}

export type Session =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; user: SessionUser };

export interface SessionContextValue {
  session: Session;
  signIn: () => void;
  signOut: () => void;
  /** True while the server session (`/api/auth/me`) is loading for a signed-in account. */
  isRoleLoading: boolean;
}

const SessionContext = React.createContext<SessionContextValue>({
  session: { status: 'loading' },
  signIn: () => undefined,
  signOut: () => undefined,
  isRoleLoading: false
});

const APP_ROLES: readonly UserRole[] = ['admin', 'teacher', 'parent', 'student', 'pending'];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (APP_ROLES as readonly string[]).includes(value);
}

/** Map API role; null role + null isActive means DB dormant — treat as admin for UX. */
function mapServerRole(role: AppUserRole | null | undefined, isActive: boolean | null | undefined): {
  role: UserRole;
  isActive: boolean;
} {
  if (isActive === false) {
    return {
      role: isUserRole(role) ? role : 'pending',
      isActive: false
    };
  }
  if (role == null) {
    return { role: 'admin', isActive: true };
  }
  if (isUserRole(role)) {
    return { role, isActive: true };
  }
  return { role: 'pending', isActive: true };
}

/** Default in-app path for a role after sign-in. */
export function defaultPathForRole(role: UserRole, isActive = true): string {
  if (!isActive) return '/access-blocked';
  switch (role) {
    case 'admin':
    case 'teacher':
      return '/dashboard';
    case 'parent':
    case 'student':
      return '/account';
    case 'pending':
    default:
      return '/awaiting-access';
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { enabled, ready, isAuthenticated, account, login, logout } = useAuth();

  // Single-shot 401 retry ref: when `/api/auth/me` returns 401 we force one
  // token refresh + refetch before concluding signedOut. The ref prevents
  // recursive retries.
  const retriedRef = React.useRef(false);

  // Reset the retry flag whenever the authenticated account changes so a
  // new sign-in gets a fresh retry budget.
  React.useEffect(() => {
    retriedRef.current = false;
  }, [account]);

  const shouldFetchMe = enabled && ready && isAuthenticated;

  const {
    data: me,
    isLoading: meLoading,
    isFetching: meFetching,
    isError: meError,
    error: meQueryError
  } = useGetCurrentUser({
    query: {
      queryKey: getGetCurrentUserQueryKey(),
      enabled: shouldFetchMe,
      staleTime: 30_000,
      // On a 401, force a single token refresh + refetch before giving up.
      // This recovers from expired cached ID tokens that the token provider's
      // silent refresh didn't catch (e.g. race with account change).
      retry: (failureCount: number, error: unknown) => {
        if (failureCount >= 1) return false;
        if (error instanceof ApiError && error.status === 401) {
          if (retriedRef.current) return false;
          retriedRef.current = true;
          return true;
        }
        return false;
      }
    }
  });

  const value = React.useMemo<SessionContextValue>(() => {
    let session: Session;
    let isRoleLoading = false;

    if (!enabled) {
      // Local dev mode — no identity provider configured.
      session = {
        status: 'signedIn',
        user: {
          name: 'Local Developer',
          email: 'dev@localhost',
          initials: 'LD',
          role: 'admin',
          isActive: true,
          appUserId: null
        }
      };
    } else if (!ready) {
      session = { status: 'loading' };
    } else if (!isAuthenticated || !account) {
      session = { status: 'signedOut' };
    } else if (meLoading || (meFetching && !me && !meError)) {
      // Authenticated with MSAL but waiting on server role — avoid flashing wrong UI.
      session = { status: 'loading' };
      isRoleLoading = true;
    } else if (meError || !me) {
      // Token may be invalid or API unreachable — stay signed-in with pending so
      // we do not grant staff chrome; user can retry or sign out.
      const name = account.name || account.email || 'User';
      const status = meQueryError instanceof ApiError ? meQueryError.status : undefined;

      if (status === 401) {
        // After the single-shot retry also fails with 401, the session is
        // genuinely signed out.
        session = { status: 'signedOut' };
      } else if (status === 403) {
        // Deactivated accounts are rejected by ensureAppUser with 403.
        session = {
          status: 'signedIn',
          user: {
            name,
            email: account.email ?? '',
            initials: initialsOf(name),
            role: 'pending',
            isActive: false,
            appUserId: null
          }
        };
      } else {
        session = {
          status: 'signedIn',
          user: {
            name,
            email: account.email ?? '',
            initials: initialsOf(name),
            role: 'pending',
            isActive: true,
            appUserId: null
          }
        };
      }
    } else {
      // Prefer live Entra account display over cached server fields.
      const name = account.name || me.displayName || account.email || 'User';
      const mapped = mapServerRole(me.role, me.isActive);
      session = {
        status: 'signedIn',
        user: {
          name,
          email: account.email || me.email || '',
          initials: initialsOf(name),
          role: mapped.role,
          isActive: mapped.isActive,
          appUserId: me.appUserId ?? null
        }
      };
    }

    return {
      session,
      signIn: () => login(),
      signOut: () => logout(),
      isRoleLoading
    };
  }, [
    enabled,
    ready,
    isAuthenticated,
    account,
    login,
    logout,
    me,
    meLoading,
    meFetching,
    meError,
    meQueryError
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  return React.useContext(SessionContext);
}

export function useUser(): SessionUser | null {
  const { session } = useSession();
  return session.status === 'signedIn' ? session.user : null;
}
